/* ══════════════════════════════════════════════════
 *  ClientPortal — /admin/client-portal
 *  4 sub-tabs: Dashboard, Portal Users, Portal Branding, Audit Log
 * ══════════════════════════════════════════════════ */
import { useState } from 'react';
import css from '../AdminPage.module.css';
import { useToast } from '../../../hooks/useToast';
import {
  usePortalDashboardStats,
  usePortalUsers,
  useDeactivatePortalUser,
  useReactivatePortalUser,
  useResetPortalUserPassword,
  usePortalUserSessions,
  useForceLogoutPortalUser,
  useDeletePortalUser,
  usePortalEmailTemplates,
  useUpdatePortalEmailTemplate,
  usePreviewPortalEmailTemplate,
  usePortalBranding,
  useUpdatePortalBranding,
  usePortalAuditLog,
} from '../../../hooks/useAdmin';
import { usePermissions } from '../../../hooks/usePermissions';
import RichEmailEditor from '../../../components/common/RichEmailEditor';
import type { PortalEmailTemplate, PortalBranding } from '../../../types/admin';

type SubTab = 'dashboard' | 'users' | 'branding' | 'audit';

export default function ClientPortal() {
  const [tab, setTab] = useState<SubTab>('dashboard');

  const tabs: { id: SubTab; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'users', label: 'Portal Users', icon: '👥' },
    { id: 'branding', label: 'Portal Branding', icon: '🎨' },
    { id: 'audit', label: 'Audit Log', icon: '📋' },
  ];

  return (
    <div className={css.section}>
      <div className={css.sectionHeader}>
        <div>
          <h2 className={css.sectionTitle}>🌐 Client Portal</h2>
          <p className={css.sectionSubtitle}>
            Manage portal users, branding, email templates and audit logs
          </p>
        </div>
      </div>

      {/* Sub-tab navigation */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 24,
          borderBottom: '2px solid var(--border-primary)',
          paddingBottom: 0,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--primary-600)' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? 'var(--primary-700)' : 'var(--text-secondary)',
              fontSize: '0.9rem',
              marginBottom: -2,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'users' && <PortalUsersTab />}
      {tab === 'branding' && <PortalBrandingTab />}
      {tab === 'audit' && <AuditLogTab />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
 *  1. DASHBOARD TAB
 * ═══════════════════════════════════════════════════ */

function DashboardTab() {
  const { data: stats, isLoading } = usePortalDashboardStats();

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  if (!stats) return <p>No data available</p>;

  const funnel = stats.invitation_funnel;
  const conversionRate =
    funnel.total > 0 ? ((funnel.accepted / funnel.total) * 100).toFixed(1) : '0';
  const avgDurationSec =
    stats.avg_page_view_duration_ms > 0 ? (stats.avg_page_view_duration_ms / 1000).toFixed(1) : '—';

  return (
    <div>
      {/* Stats cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 28,
        }}
      >
        <StatCard label="Active Users" value={stats.active_users} icon="👤" />
        <StatCard
          label="Monthly Logins"
          value={stats.monthly_unique_logins}
          icon="🔑"
          subtitle="Unique (30 days)"
        />
        <StatCard
          label="Avg Page View"
          value={`${avgDurationSec}s`}
          icon="⏱️"
          subtitle="Last 30 days"
        />
        <StatCard
          label="Invitations Sent"
          value={funnel.total}
          icon="📨"
          subtitle={`${conversionRate}% accepted`}
        />
      </div>

      {/* Invitation funnel */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Invitation Funnel</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <FunnelBadge
            label="Accepted"
            count={funnel.accepted}
            colour="var(--success-600, #16a34a)"
          />
          <FunnelBadge
            label="Pending"
            count={funnel.pending}
            colour="var(--warning-600, #d97706)"
          />
          <FunnelBadge
            label="Expired"
            count={funnel.expired}
            colour="var(--text-tertiary, #9ca3af)"
          />
          <FunnelBadge label="Revoked" count={funnel.revoked} colour="var(--danger-600, #dc2626)" />
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <h3 style={{ fontSize: '0.95rem', marginBottom: 12 }}>Recent Activity</h3>
        {stats.recent_activity.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>No recent activity</p>
        ) : (
          <table className={css.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_activity.map((a) => (
                <tr key={a.id}>
                  <td>{a.display_name || '—'}</td>
                  <td>
                    <code style={{ fontSize: '0.8rem' }}>{a.action}</code>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {a.resource_type ? `${a.resource_type}/${a.resource_id}` : '—'}
                  </td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(a.created_at).toLocaleString('en-GB')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  subtitle,
}: {
  label: string;
  value: string | number;
  icon: string;
  subtitle?: string;
}) {
  return (
    <div
      style={{
        padding: 20,
        borderRadius: 10,
        border: '1px solid var(--border-primary)',
        background: 'var(--surface-primary)',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 2 }}>
        {label}
      </div>
      {subtitle && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

function FunnelBadge({ label, count, colour }: { label: string; count: number; colour: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        borderRadius: 8,
        border: '1px solid var(--border-primary)',
        background: 'var(--surface-primary)',
      }}
    >
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: colour }} />
      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{count}</span>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
 *  2. PORTAL USERS TAB
 * ═══════════════════════════════════════════════════ */

function PortalUsersTab() {
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const { data, isLoading } = usePortalUsers({
    search: search || undefined,
    status: statusFilter || undefined,
    page,
  });
  const deactivate = useDeactivatePortalUser();
  const reactivate = useReactivatePortalUser();
  const resetPassword = useResetPortalUserPassword();
  const forceLogout = useForceLogoutPortalUser();
  const deleteUser = useDeletePortalUser();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('delete_portal_users');
  const { data: sessionsData } = usePortalUserSessions(selectedUserId);

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  async function handleDeactivate(userId: number) {
    if (!confirm('Deactivate this portal user? They will lose access immediately.')) return;
    try {
      await deactivate.mutateAsync(userId);
      showToast('User deactivated', 'success');
    } catch {
      showToast('Failed to deactivate user', 'error');
    }
  }

  async function handleReactivate(userId: number) {
    try {
      await reactivate.mutateAsync(userId);
      showToast('User reactivated', 'success');
    } catch {
      showToast('Failed to reactivate user', 'error');
    }
  }

  async function handleResetPassword(userId: number) {
    if (!confirm("Reset this user's password? A new temporary password will be emailed to them."))
      return;
    try {
      await resetPassword.mutateAsync(userId);
      showToast('Password reset & email sent', 'success');
    } catch {
      showToast('Failed to reset password', 'error');
    }
  }

  async function handleForceLogout(userId: number) {
    if (!confirm('Force logout this user from all sessions?')) return;
    try {
      await forceLogout.mutateAsync(userId);
      showToast('All sessions terminated', 'success');
    } catch {
      showToast('Failed to force logout', 'error');
    }
  }

  async function handleDeleteUser(userId: number) {
    if (
      !confirm(
        'PERMANENTLY DELETE this portal user? This will remove their Cognito account and all portal data. This action cannot be undone.',
      )
    )
      return;
    try {
      await deleteUser.mutateAsync(userId);
      showToast('Portal user permanently deleted', 'success');
    } catch {
      showToast('Failed to delete portal user', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className={css.toolbar} style={{ marginBottom: 16 }}>
        <div className={css.searchWrapper}>
          <span className={css.searchIcon}>🔍</span>
          <input
            className={css.searchInput}
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className={css.formSelect}
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
          {total} user{total !== 1 ? 's' : ''}
        </span>
      </div>

      {users.length === 0 ? (
        <div className={css.emptyState}>
          <div className={css.emptyIcon}>👥</div>
          <p className={css.emptyMessage}>No portal users found</p>
        </div>
      ) : (
        <>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>
                    {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{u.email || '—'}</td>
                  <td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: u.is_active
                          ? 'var(--success-100, #dcfce7)'
                          : 'var(--danger-100, #fee2e2)',
                        color: u.is_active
                          ? 'var(--success-700, #15803d)'
                          : 'var(--danger-700, #b91c1c)',
                      }}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString('en-GB')
                      : 'Never'}
                  </td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(u.created_at).toLocaleDateString('en-GB')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button
                        className={`${css.btnSecondary} ${css.btnSmall}`}
                        onClick={() => setSelectedUserId(selectedUserId === u.id ? null : u.id)}
                      >
                        Sessions
                      </button>
                      <button
                        className={`${css.btnSecondary} ${css.btnSmall}`}
                        onClick={() => handleResetPassword(u.id)}
                      >
                        Reset Pwd
                      </button>
                      <button
                        className={`${css.btnDanger} ${css.btnSmall}`}
                        onClick={() => handleForceLogout(u.id)}
                      >
                        Logout
                      </button>
                      {u.is_active ? (
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          onClick={() => handleDeactivate(u.id)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className={`${css.btnPrimary} ${css.btnSmall}`}
                          onClick={() => handleReactivate(u.id)}
                        >
                          Reactivate
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          style={{ background: '#7f1d1d' }}
                          onClick={() => handleDeleteUser(u.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Sessions drawer for selected user */}
          {selectedUserId && sessionsData && (
            <div
              style={{
                marginTop: 12,
                padding: 16,
                border: '1px solid var(--border-primary)',
                borderRadius: 8,
                background: 'var(--surface-secondary)',
              }}
            >
              <h4 style={{ margin: '0 0 8px', fontSize: '0.9rem' }}>
                Sessions for user #{selectedUserId}
                <button
                  type="button"
                  onClick={() => setSelectedUserId(null)}
                  style={{
                    float: 'right',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                  }}
                >
                  ×
                </button>
              </h4>
              {sessionsData.sessions.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                  No sessions found
                </p>
              ) : (
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>User Agent</th>
                      <th>Active</th>
                      <th>Created</th>
                      <th>Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsData.sessions.map((s) => (
                      <tr key={s.id}>
                        <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {s.ip_address || '—'}
                        </td>
                        <td
                          style={{
                            fontSize: '0.75rem',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {s.user_agent || '—'}
                        </td>
                        <td>{s.is_active ? '✅' : '❌'}</td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {new Date(s.created_at).toLocaleString('en-GB')}
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {new Date(s.last_used_at).toLocaleString('en-GB')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className={css.btnSecondary}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Prev
              </button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className={css.btnSecondary}
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
 *  3. PORTAL BRANDING TAB
 *  Combined: branding settings + email templates
 * ═══════════════════════════════════════════════════ */

function PortalBrandingTab() {
  const { showToast } = useToast();

  // Branding
  const { data: branding, isLoading: brandingLoading } = usePortalBranding();
  const updateBranding = useUpdatePortalBranding();
  const [brandForm, setBrandForm] = useState<Partial<PortalBranding> | null>(null);

  // Email Templates
  const { data: templates = [], isLoading: templatesLoading } = usePortalEmailTemplates();
  const updateTemplate = useUpdatePortalEmailTemplate();
  const previewTemplate = usePreviewPortalEmailTemplate();
  const [editingTemplate, setEditingTemplate] = useState<PortalEmailTemplate | null>(null);
  const [tName, setTName] = useState('');
  const [tSubject, setTSubject] = useState('');
  const [tBody, setTBody] = useState('');
  const [tTextBody, setTTextBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');

  // Initialise brand form when data loads
  const brand = brandForm ?? branding;

  function openEditTemplate(t: PortalEmailTemplate) {
    setEditingTemplate(t);
    setTName(t.name);
    setTSubject(t.subject);
    setTBody(t.html_body);
    setTTextBody(t.text_body || '');
    setPreviewHtml('');
  }

  async function saveBranding() {
    if (!brand?.firm_name) {
      showToast('Firm name is required', 'error');
      return;
    }
    try {
      await updateBranding.mutateAsync(brand);
      setBrandForm(null);
      showToast('Branding updated', 'success');
    } catch {
      showToast('Failed to update branding', 'error');
    }
  }

  async function saveTemplate() {
    if (!editingTemplate) return;
    try {
      await updateTemplate.mutateAsync({
        id: editingTemplate.id,
        name: tName,
        subject: tSubject,
        html_body: tBody,
        text_body: tTextBody || undefined,
      });
      setEditingTemplate(null);
      showToast('Template updated', 'success');
    } catch {
      showToast('Failed to update template', 'error');
    }
  }

  async function handlePreview() {
    if (!editingTemplate) return;
    try {
      const result = await previewTemplate.mutateAsync(editingTemplate.id);
      setPreviewHtml(result.html);
    } catch {
      showToast('Failed to preview', 'error');
    }
  }

  if (brandingLoading || templatesLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div>
      {/* ── Branding Settings ── */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: '0.95rem', marginBottom: 16 }}>🎨 Branding Settings</h3>
        <div className={css.grid2} style={{ marginBottom: 12 }}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Firm Name</label>
            <input
              className={css.formInput}
              value={brand?.firm_name || ''}
              onChange={(e) => setBrandForm({ ...brand, firm_name: e.target.value })}
            />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Primary Colour</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={brand?.primary_colour || '#1e3a5f'}
                onChange={(e) => setBrandForm({ ...brand, primary_colour: e.target.value })}
                style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
              />
              <input
                className={css.formInput}
                style={{ flex: 1 }}
                value={brand?.primary_colour || '#1e3a5f'}
                onChange={(e) => setBrandForm({ ...brand, primary_colour: e.target.value })}
                placeholder="#1e3a5f"
              />
            </div>
          </div>
        </div>
        <div className={css.grid2} style={{ marginBottom: 12 }}>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Portal URL</label>
            <input
              className={css.formInput}
              value={brand?.portal_url || ''}
              onChange={(e) => setBrandForm({ ...brand, portal_url: e.target.value })}
              placeholder="https://portal.yourfirm.com"
            />
          </div>
          <div className={css.formGroup}>
            <label className={css.formLabel}>Support Email</label>
            <input
              className={css.formInput}
              value={brand?.support_email || ''}
              onChange={(e) => setBrandForm({ ...brand, support_email: e.target.value })}
              placeholder="support@yourfirm.com"
            />
          </div>
        </div>
        <div className={css.formGroup} style={{ marginBottom: 12 }}>
          <label className={css.formLabel}>Logo URL</label>
          <input
            className={css.formInput}
            value={brand?.logo_url || ''}
            onChange={(e) => setBrandForm({ ...brand, logo_url: e.target.value })}
            placeholder="https://yourfirm.com/logo.png"
          />
        </div>
        {brand?.logo_url && (
          <div style={{ marginBottom: 12 }}>
            <img
              src={brand.logo_url}
              alt="Logo preview"
              style={{ maxHeight: 60, borderRadius: 4 }}
            />
          </div>
        )}
        <button
          className={css.btnPrimary}
          onClick={saveBranding}
          disabled={updateBranding.isPending}
        >
          {updateBranding.isPending ? 'Saving...' : 'Save Branding'}
        </button>
      </div>

      {/* ── Email Templates ── */}
      <div>
        <h3 style={{ fontSize: '0.95rem', marginBottom: 16 }}>✉️ Portal Email Templates</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', marginBottom: 12 }}>
          Customise the emails sent to clients when inviting, resending credentials, or resetting
          passwords. Use {'{{mergeField}}'} syntax for dynamic content.
        </p>

        <table className={css.table}>
          <thead>
            <tr>
              <th>Template</th>
              <th>Subject</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id}>
                <td style={{ fontWeight: 600 }}>{t.name}</td>
                <td
                  style={{
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.subject}
                </td>
                <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                  {t.updated_at ? new Date(t.updated_at).toLocaleDateString('en-GB') : '—'}
                </td>
                <td>
                  <button
                    className={`${css.btnSecondary} ${css.btnSmall}`}
                    onClick={() => openEditTemplate(t)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Template Edit Modal ── */}
      {editingTemplate && (
        <div className={css.modalBackdrop} onClick={() => setEditingTemplate(null)}>
          <div className={css.modalLg} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>Edit: {editingTemplate.name}</h3>
              <button className={css.modalClose} onClick={() => setEditingTemplate(null)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Template Name</label>
                  <input
                    className={css.formInput}
                    value={tName}
                    onChange={(e) => setTName(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Subject</label>
                  <input
                    className={css.formInput}
                    value={tSubject}
                    onChange={(e) => setTSubject(e.target.value)}
                  />
                </div>
              </div>

              {/* Available merge fields */}
              {editingTemplate.merge_fields && editingTemplate.merge_fields.length > 0 && (
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Available Merge Fields</label>
                  <div className={css.tagList}>
                    {editingTemplate.merge_fields.map((f) => (
                      <span
                        key={f}
                        className={css.tag}
                        style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                      >{`{{${f}}}`}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className={css.formGroup}>
                <label className={css.formLabel}>HTML Body</label>
                <RichEmailEditor
                  value={tBody}
                  onChange={setTBody}
                  placeholder="Email body HTML..."
                  mergeFields={editingTemplate.merge_fields || []}
                  height={280}
                />
              </div>

              <div className={css.formGroup}>
                <label className={css.formLabel}>Plain Text Fallback (optional)</label>
                <textarea
                  className={css.formInput}
                  style={{ minHeight: 100, fontFamily: 'monospace', fontSize: '0.82rem' }}
                  value={tTextBody}
                  onChange={(e) => setTTextBody(e.target.value)}
                  placeholder="Plain text version..."
                />
              </div>

              {/* Preview */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  className={css.btnSecondary}
                  onClick={handlePreview}
                  disabled={previewTemplate.isPending}
                >
                  {previewTemplate.isPending ? 'Loading...' : '👁️ Preview with sample data'}
                </button>
              </div>
              {previewHtml && (
                <div
                  style={{
                    marginTop: 12,
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    padding: 16,
                    background: '#fff',
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditingTemplate(null)}>
                Cancel
              </button>
              <button
                className={css.btnPrimary}
                onClick={saveTemplate}
                disabled={!tName || !tSubject || updateTemplate.isPending}
              >
                {updateTemplate.isPending ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
 *  4. AUDIT LOG TAB
 * ═══════════════════════════════════════════════════ */

function AuditLogTab() {
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = usePortalAuditLog({
    action: actionFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 50);
  const actionTypes = data?.action_types ?? [];

  function exportCsv() {
    const params = new URLSearchParams();
    if (actionFilter) params.set('action', actionFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    window.open(`/api/portal-admin/audit-log/export?${params.toString()}`, '_blank');
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className={css.toolbar} style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          className={css.formSelect}
          style={{ width: 180 }}
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Actions</option>
          {actionTypes.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>From</label>
          <input
            type="date"
            className={css.formInput}
            style={{ width: 150 }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
          <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>To</label>
          <input
            type="date"
            className={css.formInput}
            style={{ width: 150 }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <button className={css.btnSecondary} onClick={exportCsv}>
          📥 Export CSV
        </button>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>{total} entries</span>
      </div>

      {logs.length === 0 ? (
        <div className={css.emptyState}>
          <div className={css.emptyIcon}>📋</div>
          <p className={css.emptyMessage}>No audit entries found</p>
        </div>
      ) : (
        <>
          <table className={css.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>IP</th>
                <th>Duration</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id}>
                  <td>{l.display_name || '—'}</td>
                  <td>
                    <code style={{ fontSize: '0.8rem' }}>{l.action}</code>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {l.resource_type ? `${l.resource_type}/${l.resource_id}` : '—'}
                  </td>
                  <td style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    {l.ip_address || '—'}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {l.page_view_duration_ms
                      ? `${(l.page_view_duration_ms / 1000).toFixed(1)}s`
                      : '—'}
                  </td>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {new Date(l.created_at).toLocaleString('en-GB')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className={css.btnSecondary}
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Prev
              </button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                Page {page} of {totalPages}
              </span>
              <button
                className={css.btnSecondary}
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
